import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import { FACILITY_PAGE_SIZE } from "@/lib/hcbb-cost-estimator-utils";

export type HcbbResultTab = "facilities" | "professionals";

/**
 * How much of each result list the member has revealed, and which tab they are
 * on.
 *
 * This is view state, not data: the rows themselves stay in the React Query
 * cache, which survives a trip to a detail screen on its own. Only these
 * counters die with the unmounting results page, so they live here — long
 * enough to walk to a detail screen and back, and no longer.
 */
export type HcbbResultViewState = {
  /** The search these counters describe — see `toHcbbResultKey`. */
  resultKey: string | null;
  /** Null until the member picks one: the results decide which tab opens. */
  activeTab: HcbbResultTab | null;
  visibleFacilities: number;
  /** Reveal depth into the unfiltered professional candidate list. */
  visibleBatches: number;
  /**
   * Reveal depth into the *filtered* professional candidate list — kept
   * separate from `visibleBatches` so clearing a filter restores the
   * unfiltered list's own previously-revealed depth instead of resetting to
   * page one, and so filtering doesn't lose the unfiltered depth either.
   */
  filteredVisibleBatches: number;
};

export const INITIAL_HCBB_RESULT_VIEW: HcbbResultViewState = {
  resultKey: null,
  activeTab: null,
  visibleFacilities: FACILITY_PAGE_SIZE,
  visibleBatches: 1,
  filteredVisibleBatches: 1,
};

/**
 * Rebases the view on a new search before applying a change.
 *
 * Every write carries the key it belongs to, so a new search is recognised on
 * its first write rather than through a reset step somewhere else that a later
 * change could forget to fire.
 */
const forKey = (state: HcbbResultViewState, resultKey: string) => {
  if (state.resultKey === resultKey) return;

  state.resultKey = resultKey;
  state.activeTab = INITIAL_HCBB_RESULT_VIEW.activeTab;
  state.visibleFacilities = INITIAL_HCBB_RESULT_VIEW.visibleFacilities;
  state.visibleBatches = INITIAL_HCBB_RESULT_VIEW.visibleBatches;
  state.filteredVisibleBatches =
    INITIAL_HCBB_RESULT_VIEW.filteredVisibleBatches;
};

const hcbbResultViewSlice = createSlice({
  name: "hcbbResultView",
  initialState: INITIAL_HCBB_RESULT_VIEW,
  reducers: {
    setHcbbActiveTab: (
      state,
      action: PayloadAction<{ resultKey: string; tab: HcbbResultTab }>,
    ) => {
      forKey(state, action.payload.resultKey);
      state.activeTab = action.payload.tab;
    },
    setHcbbVisibleFacilities: (
      state,
      action: PayloadAction<{ resultKey: string; count: number }>,
    ) => {
      forKey(state, action.payload.resultKey);
      state.visibleFacilities = action.payload.count;
    },
    setHcbbVisibleBatches: (
      state,
      action: PayloadAction<{ resultKey: string; count: number }>,
    ) => {
      forKey(state, action.payload.resultKey);
      state.visibleBatches = action.payload.count;
    },
    setHcbbFilteredVisibleBatches: (
      state,
      action: PayloadAction<{ resultKey: string; count: number }>,
    ) => {
      forKey(state, action.payload.resultKey);
      state.filteredVisibleBatches = action.payload.count;
    },
  },
});

export const {
  setHcbbActiveTab,
  setHcbbVisibleFacilities,
  setHcbbVisibleBatches,
  setHcbbFilteredVisibleBatches,
} = hcbbResultViewSlice.actions;

/**
 * Named, unlike the older slices' default export: the standards ratchet
 * counts default exports outside route pages, and a name is what the store
 * imports anyway.
 */
export const hcbbResultViewReducer = hcbbResultViewSlice.reducer;
